import { Button, SegmentedControl } from 'AWS-UI-Components-React';
import React from 'react';
import { SIMULATION_DETAILS_VIEW, SIMULATOR_VIEW_SEGMENTS } from '../../constants/views';
import logger from '../../utils/logging';
import { getJSClient } from '../js_client';
import { ApiInvocationStatus } from '../../constants/constants';
import { NewSimulationWindow } from './new_simulation_window';
import { getLatestExecutionIdPrefix } from './validation_test_results/utils';

import { displayStatusBadge, millisToMinutesAndSeconds, sorter } from './simulator_utils';
import { SimulationDetailsView } from './simulation_details_view';
import { TEST_RESULT_TRANSITION_TIMEOUT } from './validation_test_results/constants';

export class SimulatorPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      access: this.props.identity.access,
      user: this.props.identity.username,
      selectedSimulation: undefined,
      simulations: undefined,
      refreshIndex: 0,
      showNewSimulationWindow: false,
      selectedSegmentId: SIMULATION_DETAILS_VIEW.id,
      selectedSimulationRuntime: undefined,
      status: null,
      productLines: this.props.productLines,
      productLinesByIdMap: this.props.productLinesByIdMap,
    };
    this.pageSize = 9;
    this.client = getJSClient();
  }

  componentDidMount = () => {
    document.title = 'Phoenix : Simulator';
    this.client = getJSClient();
    this.getSimulationsList();
    this.client.getComponentConfig(this.onConfigFetchSuccess, this.onConfigFetchFailure, false);
  };

  onConfigFetchSuccess = (response) => {
    try {
      const components = [];
      for (const component of JSON.parse(response.componentConfig)) {
        const id = component.id.trim();
        components.push({ label: id, value: id, id });
      }
      this.setState({ components });
    } catch (e) {
      logger.error('Error parsing config response', e);
    }
  }

  onConfigFetchFailure = (errorResponse) => {
    logger.error('Failure to fetch config', errorResponse);
  };

  onGetSimulationsSuccess = (data) => {
    if (data.simulations) {
      data.simulations.sort(sorter);
    }

    this.setState({ simulations: data.simulations }, this.selectLatestSimulation(data.simulations));
  };

  onGetSimulationsFailure = (error) => {
    logger.error('Error getting simulations', error);
  };

  getSimulationsList = () => {
    this.client.getSimulations(this.onGetSimulationsSuccess, this.onGetSimulationsFailure, {});
  };

  selectLatestSimulation = (simulations) => {
    if (simulations && simulations.length) {
      // simulations is already sorted newest first, so pick the newest one still visible under
      // the active filter rather than the newest overall.
      const visible = this.applyFilteringTokens(simulations);
      const latestSimulation = visible.length ? visible[0] : simulations[0];
      if (latestSimulation) {
        this.onSelection(latestSimulation);
      }
    }
  };

  applyFilteringTokens = (simulations) => {
    const { filteringTokens } = this.state;
    if (!filteringTokens || !filteringTokens.length) {
      return simulations;
    }
    return simulations.filter((simulation) => simulationFilteringFunction(simulation, filteringTokens, 'and'));
  };

  loadValidationTestResults = (payloadParams, callbackOnSuccess) => {
    const payload = {
      snapshotId: payloadParams.snapshotId,
      executionId: payloadParams.executionId,
      prefixLookup: Boolean(payloadParams.prefixLookup),
    };
    this.client.getValidationTestResults(
      (response) => this.onGetValidationTestResultsSuccess(response, callbackOnSuccess),
      this.onGetValidationTestResultsFailure,
      payload
    );
  };

  onGetValidationTestResultsSuccess = (response, callbackOnSuccess) => {
    const { validationTestResults } = this.state;

    if (response && response.testResults) {
      const { testResults } = response;

      const resultsById = testResults.reduce((acc, result) => {
        const { snapshotId, executionId } = result;
        acc[`${snapshotId}${executionId}`] = result;

        return acc;
      }, {});

      const updatedResults = validationTestResults.map((result) => {
        const { snapshotId, executionId } = result;
        return resultsById[`${snapshotId}${executionId}`] || result;
      });

      if (callbackOnSuccess) {
        this.setState({ validationTestResults: updatedResults }, callbackOnSuccess());
      } else {
        this.setState({ validationTestResults: updatedResults });
      }
    }
  };

  onGetValidationTestResultsFailure = (error) => {
    logger.error('Error getting validation tests results', error);
  };

  columnDefinitions = () => {
    return [
      {
        id: 'name',
        header: 'Simulation',
        cell: (item) => (
          <span>
            {item.name} {displayStatusBadge(item)}
          </span>
        ),
      },
    ];
  };

  getFeatures = () => {
    let features = ['selection', 'propertyFiltering'];
    if (this.state.simulations && this.state.simulations.length > this.pageSize) {
      features.push('pagination');
    }
    return features;
  };

  onSelection = (selected) => {
    if (selected) {
      this.getSimulation(selected);
    } else {
      this.setState({ selectedSimulation: undefined });
    }
  };

  newSimulationWindow = () => {
    return (
      <NewSimulationWindow
        onCancel={this.onCancelSimulation}
        onSubmit={this.onRunSimulation}
        identity={this.props.identity}
        user={this.state.user}
        components={this.state.components}
        productLines={this.state.productLines}
      />
    );
  };

  beginNewSimulation = () => {
    this.setState({ showNewSimulationWindow: true });
  };

  onCancelSimulation = () => {
    this.setState({ showNewSimulationWindow: false });
  };

  onRunSimulation = () => {
    // Surface the simulation that was just triggered: filter the list down to its owner, so the
    // newest run sits on top instead of being buried among every other user's simulations.
    const { user } = this.state;
    const filteringTokens = user
      ? [{ propertyKey: 'user', propertyLabel: 'User', value: user, label: user, negated: false }]
      : [];
    this.setState({ showNewSimulationWindow: false, filteringTokens }, this.getSimulationsList);
  };

  onFilteringTokensChange = (filteringTokens) => {
    this.setState({ filteringTokens });
  };

  onRefresh = () => {
    this.setState({ refreshIndex: this.state.refreshIndex + 1 });
  };

  onSegmentChange = (event) => {
    this.setState({ selectedSegmentId: event.detail.selectedId });
  };

  getSimulation = (simulation) => {
    this.setState(
      { status: ApiInvocationStatus.IN_PROGRESS, selectedSimulation: simulation },
      this.client.getSimulations(this.onGetSimulationSuccess, this.onGetSimulationFailure, { id: simulation.id })
    );
  };

  onGetSimulationSuccess = (data) => {
    const { simulations, validationTestResults } = data;
    this.setSimulationRuntime(simulations);

    this.setState(
      {
        selectedSimulationDetails: simulations,
        validationTestResults,
        status: ApiInvocationStatus.SUCCEEDED,
        selectedSegmentId: SIMULATION_DETAILS_VIEW.id,
      },
      () => setTimeout(() => this.loadStepsOfLatestValidationTestRun(), TEST_RESULT_TRANSITION_TIMEOUT)
    );
  };

  onGetSimulationFailure = (error) => {
    logger.error('Error getting simulation details', error);
    this.setState({
      status: ApiInvocationStatus.FAILED,
    });
  };

  loadStepsOfLatestValidationTestRun = () => {
    const { selectedSimulationDetails, validationTestResults } = this.state;
    if (!validationTestResults || !validationTestResults.length) {
      return;
    }
    const latestExecutionIdPrefix = getLatestExecutionIdPrefix(validationTestResults);
    const stepsAlreadyLoaded = validationTestResults.some(
      (result) =>
        result.executionId.startsWith(latestExecutionIdPrefix.toString()) && result.steps && result.steps.length
    );
    if (stepsAlreadyLoaded) {
      return;
    }

    const snapshotId =
      selectedSimulationDetails && selectedSimulationDetails.length ? selectedSimulationDetails[0].id : '';

    const payload = { snapshotId, executionId: latestExecutionIdPrefix.toString(), prefixLookup: true };
    this.loadValidationTestResults(payload);
  };

  setSimulationRuntime = (simulations) => {
    let statusMap = new Map();
    for (const sim of simulations) {
      const prefix = sim.status.toString().split(' - ')[0];
      if (statusMap.has(prefix)) {
        statusMap.get(prefix).push(sim.timestamp);
      } else {
        statusMap.set(prefix, [sim.timestamp]);
      }
    }
    let runtimeMap = new Map();
    for (let [key, value] of statusMap.entries()) {
      if (value.length > 1) {
        const runtime = Date.parse(value[1]) - Date.parse(value[0]);
        runtimeMap.set(key, millisToMinutesAndSeconds(runtime));
      }
    }
    this.setState({ selectedSimulationRuntime: runtimeMap });
  };

  render() {
    const {
      simulations,
      user,
      selectedSimulation,
      selectedSegmentId,
      refreshIndex,
      status,
      selectedSimulationDetails,
      validationTestResults,
      selectedSimulationRuntime,
      filteringTokens,
    } = this.state;

    return (
      <div className="awsui-util-container">
        {this.state.showNewSimulationWindow && this.newSimulationWindow()}
        <div className="awsui-util-container-header">
          <div className="awsui-util-action-stripe">
            <div className="awsui-util-action-stripe-title">
              <h2>Simulator</h2>
            </div>
            <div className="awsui-util-action-stripe-group">
              {selectedSimulation && (
                <SegmentedControl
                  options={SIMULATOR_VIEW_SEGMENTS}
                  selectedId={selectedSegmentId ? selectedSegmentId : SIMULATION_DETAILS_VIEW.id}
                  onChange={this.onSegmentChange}
                />
              )}
            </div>
            <div className="awsui-util-action-stripe-group">
              {selectedSimulation && <Button onClick={this.onRefresh}>Refresh</Button>}
              <Button onClick={this.beginNewSimulation} variant={'primary'}>
                New Simulation
              </Button>
            </div>
          </div>
        </div>

        <SimulationDetailsView
          simulations={simulations}
          user={user}
          selectedSimulation={selectedSimulation}
          onSelection={this.onSelection}
          identity={this.props.identity}
          refreshIndex={refreshIndex}
          selectedSegmentId={selectedSegmentId}
          status={status}
          selectedSimulationDetails={selectedSimulationDetails}
          validationTestResults={validationTestResults}
          selectedSimulationRuntime={selectedSimulationRuntime}
          loadValidationTestResults={this.loadValidationTestResults}
          productLinesByIdMap={this.state.productLinesByIdMap}
          filteringTokens={filteringTokens}
          onFilteringTokensChange={this.onFilteringTokensChange}
        />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/simulator/simulator_page.jsx